import type { CarFromLicensePlateRecord } from '@navio/nsdk';
import { NavioInfoCard } from 'noddi-ui-web';
import { COUNTRY_OPTIONS, countryCodeToFlag, NoddiDateFormats, noddiFormatDate } from 'noddi-util';
import { getCommonTranslations } from '../../../utils/commonTranslations';

type Props = {
  car: CarFromLicensePlateRecord;
  /** Opens the VIN decode; omitted when the VIN could not be parsed. */
  onVinNumberClick?: () => void;
};

/** Registry facts about the car, with the EU control called out under them. */
export const CarFacts = ({ car, onVinNumberClick }: Props) => {
  const translations = getCommonTranslations();
  const texts = translations.carDetails;
  const { length, height, width, weight, vinNumber, countryCode, euControlNext, euControlPrevious } = car;
  const registeredIn = formatCountryDisplay(countryCode);
  // Next control is what the customer acts on; the last approval only stands in when no next date is known.
  const euControl = euControlNext
    ? { label: texts.nextCheck, date: euControlNext }
    : euControlPrevious && { label: texts.lastApproval, date: euControlPrevious };

  return (
    <NavioInfoCard
      title={texts.aboutTheCar}
      translations={translations.infoCard}
      items={[
        // The title shows the family name, so the registry model with its trim and power lives here.
        !!car.familyName && { label: texts.model, value: car.model },
        !!length && { label: texts.length, value: `${length} mm` },
        !!height && { label: texts.height, value: `${height} mm` },
        !!width && { label: texts.width, value: `${width} mm` },
        !!weight && { label: texts.weight, value: `${weight} kg` },
        !!registeredIn && { label: texts.firstRegisteredIn, value: registeredIn },
        !!vinNumber && {
          label: texts.vinChassi,
          copyText: vinNumber,
          value: onVinNumberClick ? (
            <button
              type='button'
              className='cursor-pointer font-mono text-primary underline hover:no-underline'
              onClick={onVinNumberClick}
            >
              {vinNumber}
            </button>
          ) : (
            <span className='font-mono'>{vinNumber}</span>
          )
        }
      ]}
    >
      {euControl && (
        <div className='flex items-center justify-between gap-4 rounded-lg bg-card-light-background px-5 py-4'>
          <div className='flex flex-col gap-1'>
            <span className='font-semibold text-primary'>{texts.euCheckDetails}</span>
            <span className='text-sm text-darkPurple70'>{euControl.label}</span>
          </div>
          <span className='text-lg font-semibold text-primary'>
            {noddiFormatDate(euControl.date, NoddiDateFormats.DATE)}
          </span>
        </div>
      )}
    </NavioInfoCard>
  );
};

const formatCountryDisplay = (code: string | null | undefined): string | undefined => {
  if (!code) {
    return undefined;
  }
  const normalized = code.toUpperCase();
  const known = COUNTRY_OPTIONS.find((option) => option.value === normalized);
  if (known) {
    return known.label;
  }
  const flag = countryCodeToFlag(normalized);
  return flag ? `${flag} ${normalized}` : normalized;
};
